// Shops in the one currency (decision 0053: "one currency, set prices, a merchant and a smith in town; selling below
// the buy price"). Prices are the item table's; a shop lists what it sells (with an optional limited stock) and the
// tags it buys, and pays a fraction of the price (`sellRate`). A discount (a fraction the game grants: the town's
// standing through its conditions) lowers the buy price; the sell price always stays below the buy price, whatever
// the discount (at most the buy price less one). Trades are all or nothing; refused, nothing changes. Stock bought out
// of a limited line is plain JSON (`ShopState`) the game keeps.
import type { Inventory, InventoryState } from "./inventory";
import type { ItemTable } from "./items";
import type { Wallet } from "./crafting";

/** A shop as data. */
export interface ShopDef {
  id: string;
  /** What it sells, in order; `count` limits a line (absent: always in stock). */
  stock: readonly { item: string; count?: number }[];
  /** Item tags it buys (any of them); absent: anything with a price. */
  buys?: readonly string[];
  /** The fraction of the price it pays (0 ≤ rate < 1). */
  sellRate: number;
}

/** How much of each limited line each shop has sold. Plain JSON. */
export interface ShopState {
  sold: Record<string, Record<string, number>>;
}

/** Why a trade was refused. */
export type TradeRefusal = "unknown" | "notSold" | "stock" | "money" | "room" | "notBought" | "count" | "equipped";

/** Planted defects beside what they break (tests only). */
export const SHOP_FAULTS = {
  /** The shop pays the full buy price: the economy check "selling below the buy price" must go red. */
  sellAtBuyPrice: false,
};

/** What a trade works on. */
export interface TradeContext {
  inv: InventoryState;
  wallet: Wallet;
  state: ShopState;
  /** The buy-price discount, 0..1 (default 0). */
  discount?: number;
  /** How many of the item may not be sold (equipped). */
  reserved?: (item: string) => number;
}

/** One trade done. */
export interface Trade {
  shop: string;
  item: string;
  count: number;
  /** Money moved: paid on a buy, received on a sale. */
  total: number;
  each: number;
}

/** Problems with shops against the item table (empty when sound). */
export function checkShops(shops: readonly ShopDef[], items: ItemTable): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const s of shops) {
    const at = `shop "${s.id}"`;
    if (seen.has(s.id)) out.push(`${at} is defined twice`);
    seen.add(s.id);
    if (!(s.sellRate >= 0 && s.sellRate < 1)) out.push(`${at}: sellRate ${s.sellRate} must be at least 0 and below 1`);
    for (const l of s.stock) {
      if (!items.has(l.item)) out.push(`${at} sells a missing item "${l.item}"`);
      else if (items.get(l.item).price === undefined) out.push(`${at} sells "${l.item}", which has no price`);
      if (l.count !== undefined && !(Number.isInteger(l.count) && l.count >= 1)) out.push(`${at}: stock of "${l.item}" is ${l.count}`);
    }
  }
  return out;
}

/** The shops over the item table and an inventory. */
export interface Shops {
  get(id: string): ShopDef | undefined;
  /** The buy price of one, with a discount (at least 1 when the price is above 0). */
  buyPrice(shop: string, item: string, discount?: number): number;
  /** What the shop pays for one: its rate of the price, always below the buy price at that discount. */
  sellPrice(shop: string, item: string, discount?: number): number;
  /** How many a shop can still sell (Infinity for an unlimited line, 0 when it doesn't sell it). */
  stockLeft(state: ShopState, shop: string, item: string): number;
  /** Whether a shop buys an item. */
  buys(shop: string, item: string): boolean;
  /** Null when the trade can be made now, else why not. */
  canBuy(ctx: TradeContext, shop: string, item: string, count: number): TradeRefusal | null;
  buy(ctx: TradeContext, shop: string, item: string, count: number): Trade | { refused: TradeRefusal };
  canSell(ctx: TradeContext, shop: string, item: string, count: number): TradeRefusal | null;
  sell(ctx: TradeContext, shop: string, item: string, count: number): Trade | { refused: TradeRefusal };
}

/** Build the shops. Throws listing every problem `checkShops` finds. */
export function createShops(items: ItemTable, inventory: Inventory, shops: readonly ShopDef[]): Shops {
  const issues = checkShops(shops, items);
  if (issues.length) throw new Error(`shops: ${issues.join("; ")}`);
  const byId = new Map(shops.map((s) => [s.id, s]));
  const line = (shop: string, item: string) => byId.get(shop)?.stock.find((l) => l.item === item);
  const price = (item: string): number | undefined => (items.has(item) ? items.get(item).price : undefined);
  const clampDiscount = (d = 0): number => Math.max(0, Math.min(1, d));
  const sh: Shops = {
    get: (id) => byId.get(id),
    buyPrice(shop, item, discount = 0) {
      const p = price(item);
      if (p === undefined || !byId.has(shop)) return Infinity;
      return p === 0 ? 0 : Math.max(1, Math.round(p * (1 - clampDiscount(discount))));
    },
    sellPrice(shop, item, discount = 0) {
      const s = byId.get(shop);
      const p = price(item);
      if (!s || p === undefined) return 0;
      // the planted defect sits here, beside the rule it breaks
      if (SHOP_FAULTS.sellAtBuyPrice) return sh.buyPrice(shop, item, discount);
      return Math.max(0, Math.min(Math.floor(p * s.sellRate), sh.buyPrice(shop, item, discount) - 1));
    },
    stockLeft(state, shop, item) {
      const l = line(shop, item);
      if (!l) return 0;
      if (l.count === undefined) return Infinity;
      return Math.max(0, l.count - (state.sold[shop]?.[item] ?? 0));
    },
    buys(shop, item) {
      const s = byId.get(shop);
      if (!s || price(item) === undefined) return false;
      return !s.buys || s.buys.some((t) => items.get(item).tags.includes(t));
    },
    canBuy(ctx, shop, item, count) {
      if (!byId.has(shop) || !items.has(item)) return "unknown";
      if (!Number.isInteger(count) || count < 1) return "count";
      if (!line(shop, item)) return "notSold";
      if (sh.stockLeft(ctx.state, shop, item) < count) return "stock";
      if (ctx.wallet.balance < sh.buyPrice(shop, item, ctx.discount) * count) return "money";
      if (inventory.room(ctx.inv, item) < count) return "room";
      return null;
    },
    buy(ctx, shop, item, count) {
      const why = sh.canBuy(ctx, shop, item, count);
      if (why) return { refused: why };
      const each = sh.buyPrice(shop, item, ctx.discount);
      ctx.wallet.balance -= each * count;
      inventory.add(ctx.inv, item, count);
      if (line(shop, item)!.count !== undefined) {
        const sold = (ctx.state.sold[shop] ??= {});
        sold[item] = (sold[item] ?? 0) + count;
      }
      return { shop, item, count, total: each * count, each };
    },
    canSell(ctx, shop, item, count) {
      if (!byId.has(shop) || !items.has(item)) return "unknown";
      if (!Number.isInteger(count) || count < 1) return "count";
      if (!sh.buys(shop, item)) return "notBought";
      const have = inventory.count(ctx.inv, item);
      if (have < count) return "count";
      if (have - (ctx.reserved?.(item) ?? 0) < count) return "equipped";
      return null;
    },
    sell(ctx, shop, item, count) {
      const why = sh.canSell(ctx, shop, item, count);
      if (why) return { refused: why };
      const each = sh.sellPrice(shop, item, ctx.discount);
      inventory.take(ctx.inv, item, count);
      ctx.wallet.balance += each * count;
      return { shop, item, count, total: each * count, each };
    },
  };
  return sh;
}
